interface TimezoneOption {
  id: string
  displayValue: string
}

const COMMON_TIMEZONES: TimezoneOption[] = [
  { id: 'America/St_Johns', displayValue: 'Newfoundland Time (St. John’s)' },
  { id: 'America/Halifax', displayValue: 'Atlantic Time (Halifax)' },
  { id: 'America/Toronto', displayValue: 'Eastern Time (Toronto)' },
  { id: 'America/New_York', displayValue: 'Eastern Time (New York)' },
  { id: 'America/Chicago', displayValue: 'Central Time (Chicago)' },
  { id: 'America/Denver', displayValue: 'Mountain Time (Denver)' },
  { id: 'America/Edmonton', displayValue: 'Mountain Time (Edmonton)' },
  { id: 'America/Phoenix', displayValue: 'Arizona Time (no DST)' },
  { id: 'America/Vancouver', displayValue: 'Pacific Time (Vancouver)' },
  { id: 'America/Los_Angeles', displayValue: 'Pacific Time (Los Angeles)' },
  { id: 'America/Anchorage', displayValue: 'Alaska Time' },
  { id: 'Pacific/Honolulu', displayValue: 'Hawaii Time' },
  { id: 'Europe/London', displayValue: 'UK / Ireland' },
  { id: 'Europe/Paris', displayValue: 'Central European (Paris, Berlin)' },
  { id: 'Europe/Athens', displayValue: 'Eastern European (Athens)' },
  { id: 'Asia/Dubai', displayValue: 'Gulf Time (Dubai)' },
  { id: 'Asia/Kolkata', displayValue: 'India Time' },
  { id: 'Asia/Singapore', displayValue: 'Singapore / Hong Kong' },
  { id: 'Asia/Tokyo', displayValue: 'Japan / Korea' },
  { id: 'Australia/Sydney', displayValue: 'Sydney / Melbourne' },
  { id: 'Pacific/Auckland', displayValue: 'New Zealand' },
  { id: 'UTC', displayValue: 'UTC' },
]

/**
 * Resolves the visitor's IANA timezone via the browser's Intl runtime. Falls
 * back to `UTC` only when the platform doesn't expose `Intl` (extremely rare
 * — old Node / sandbox tests). For modern browsers this is reliable.
 *
 * @returns {string} An IANA timezone identifier.
 */
const detectBrowserTimezone = (): string => {
  // eslint-disable-next-line new-cap
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/**
 * Returns the current UTC offset for an IANA timezone in `GMT±HH:MM` form.
 * Computed via `Intl.DateTimeFormat`'s `timeZoneName: 'longOffset'` formatter,
 * which yields `GMT-05:00`, `GMT+00:00`, etc. Used for dropdown labels so a
 * guest immediately sees how their selected view differs from the host's
 * local time.
 *
 * @param {string} ianaTimezone - The IANA timezone to introspect.
 * @returns {string} The offset string (e.g. `GMT-04:00`). Returns empty
 *   string if the runtime rejects the zone.
 */
const getTimezoneOffsetLabel = (ianaTimezone: string): string => {
  try {
    // eslint-disable-next-line new-cap
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      timeZoneName: 'longOffset',
    })
    const parts = formatter.formatToParts(new Date())
    const offsetPart = parts.find(part => part.type === 'timeZoneName')
    return offsetPart?.value ?? ''
  } catch {
    return ''
  }
}

/**
 * Derives a friendly display label for an arbitrary IANA timezone — used when
 * the host's timezone (or the visitor's detected timezone) isn't in our
 * curated `COMMON_TIMEZONES` list. Strips the region prefix and prettifies
 * the city portion (e.g. `America/Indiana/Indianapolis` → `Indianapolis`).
 *
 * @param {string} ianaTimezone - The IANA identifier.
 * @returns {string} A capitalized, underscore-replaced label suitable for
 *   display next to the offset.
 */
const buildLabelForIanaTimezone = (ianaTimezone: string): string => {
  const existing = COMMON_TIMEZONES.find(option => option.id === ianaTimezone)
  if (existing) {
    return existing.displayValue
  }
  const segments = ianaTimezone.split('/')
  const lastSegment = segments[segments.length - 1] || ianaTimezone
  return lastSegment.replace(/_/gu, ' ')
}

/**
 * Builds the dropdown option list for the booking page's "Viewing in"
 * selector. Guarantees:
 *   1. Visitor's detected timezone is always present at the top
 *   2. Host's timezone is always present (so the guest can switch back to
 *      "what the host sees" with one click even when it isn't a common zone)
 *   3. Every option's label carries the current offset (`GMT-05:00`) so the
 *      magnitude of the conversion is obvious before clicking
 *   4. The curated `COMMON_TIMEZONES` list follows after the prioritised pair
 *
 * @param {object} input - Identifiers to prioritise.
 * @param {string} input.hostTimezone - The event type's host timezone.
 * @param {string} input.viewerTimezone - The currently-selected view timezone.
 * @returns {TimezoneOption[]} Deduplicated, ordered options ready for the
 *   `<select>`.
 */
const buildTimezoneOptions = (input: {
  hostTimezone: string
  viewerTimezone: string
}): TimezoneOption[] => {
  const prioritised: string[] = []
  if (input.viewerTimezone && input.viewerTimezone.length > 0) {
    prioritised.push(input.viewerTimezone)
  }
  if (
    input.hostTimezone &&
    input.hostTimezone.length > 0 &&
    input.hostTimezone !== input.viewerTimezone
  ) {
    prioritised.push(input.hostTimezone)
  }

  const seen = new Set<string>()
  const ordered: TimezoneOption[] = []

  for (const ianaTimezone of prioritised) {
    if (seen.has(ianaTimezone)) {
      continue
    }
    seen.add(ianaTimezone)
    const baseLabel = buildLabelForIanaTimezone(ianaTimezone)
    const offset = getTimezoneOffsetLabel(ianaTimezone)
    ordered.push({
      id: ianaTimezone,
      displayValue: offset ? `${baseLabel} (${offset})` : baseLabel,
    })
  }

  for (const commonOption of COMMON_TIMEZONES) {
    if (seen.has(commonOption.id)) {
      continue
    }
    seen.add(commonOption.id)
    const offset = getTimezoneOffsetLabel(commonOption.id)
    ordered.push({
      id: commonOption.id,
      displayValue: offset
        ? `${commonOption.displayValue} (${offset})`
        : commonOption.displayValue,
    })
  }

  return ordered
}

/**
 * Compact label for showing the host's timezone in a callout next to the
 * booking page header. Combines the friendly label with the current offset.
 *
 * @param {string} ianaTimezone - The host's timezone.
 * @returns {string} e.g. `Eastern Time (Toronto) — GMT-04:00`. Falls back to
 *   the raw IANA identifier if the offset isn't computable.
 */
const formatTimezoneCalloutLabel = (ianaTimezone: string): string => {
  if (!ianaTimezone || ianaTimezone.length === 0) {
    return ''
  }
  const baseLabel = buildLabelForIanaTimezone(ianaTimezone)
  const offset = getTimezoneOffsetLabel(ianaTimezone)
  return offset ? `${baseLabel} — ${offset}` : baseLabel
}

export {
  COMMON_TIMEZONES,
  detectBrowserTimezone,
  getTimezoneOffsetLabel,
  buildLabelForIanaTimezone,
  buildTimezoneOptions,
  formatTimezoneCalloutLabel,
}
export type { TimezoneOption }
